import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '@/components/ui/dialog';
import {
  Plus, ChevronUp, ChevronDown, Trash2, Save, Tags, Activity, Search,
} from 'lucide-react';
import api from '@/utils/api';

const KIND_META = {
  interests: { label: 'Interests', icon: Tags, sub: 'Guests pick from these when they RSVP.' },
  activities: { label: 'Activities', icon: Activity, sub: 'Things guests might love to do at events.' },
};

const AdminTaxonomiesPage = () => {
  const [tab, setTab] = useState('interests');
  return (
    <div className="p-8 max-w-6xl space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold text-primary-900">Global taxonomies</h1>
        <p className="text-neutral-600 text-sm">
          Master list of interests and activities. Events can override which ones are shown on their RSVP form.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-2 max-w-sm">
          <TabsTrigger value="interests" data-testid="taxonomy-tab-interests">
            <Tags className="h-3.5 w-3.5 mr-1.5" /> Interests
          </TabsTrigger>
          <TabsTrigger value="activities" data-testid="taxonomy-tab-activities">
            <Activity className="h-3.5 w-3.5 mr-1.5" /> Activities
          </TabsTrigger>
        </TabsList>
        <TabsContent value="interests" className="pt-6">
          <TaxonomyList kind="interests" />
        </TabsContent>
        <TabsContent value="activities" className="pt-6">
          <TaxonomyList kind="activities" />
        </TabsContent>
      </Tabs>
    </div>
  );
};

const TaxonomyList = ({ kind }) => {
  const meta = KIND_META[kind];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newIcon, setNewIcon] = useState('');
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/taxonomies/${kind}?include_inactive=true`);
      setItems(data.items || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [kind]);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const needle = q.trim().toLowerCase();
    return items.filter((x) => (x.label || '').toLowerCase().includes(needle) || (x.slug || '').includes(needle));
  }, [items, q]);

  const create = async () => {
    if (!newLabel.trim()) return;
    setSaving(true);
    try {
      await api.post(`/admin/taxonomies/${kind}`, { label: newLabel.trim(), icon: newIcon.trim() || null });
      setNewLabel(''); setNewIcon('');
      setDialogOpen(false);
      await reload();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to create');
    } finally { setSaving(false); }
  };

  const patch = async (id, body) => {
    try {
      await api.patch(`/admin/taxonomies/${kind}/${id}`, body);
      setItems((cur) => cur.map((x) => (x.id === id ? { ...x, ...body } : x)));
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to update');
    }
  };

  const softDelete = async (id) => {
    if (!window.confirm('Deactivate this item? Existing registrations that reference it are unaffected.')) return;
    await api.delete(`/admin/taxonomies/${kind}/${id}`);
    await reload();
  };

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= filtered.length) return;
    // Reorder in the *full* array using the ids visible after filter
    const arr = [...items];
    const a = arr.findIndex((x) => x.id === filtered[idx].id);
    const b = arr.findIndex((x) => x.id === filtered[target].id);
    if (a < 0 || b < 0) return;
    [arr[a], arr[b]] = [arr[b], arr[a]];
    // Persist new order (send full ordered id list)
    const ids = arr.map((x) => x.id);
    setItems(arr.map((x, i) => ({ ...x, order: i })));
    await api.post(`/admin/taxonomies/${kind}/reorder`, { ids });
  };

  return (
    <Card className="border shadow-soft" data-testid={`taxonomy-list-${kind}`}>
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <div className="text-xs uppercase tracking-widest text-secondary-700 mb-1 flex items-center gap-1.5">
              <meta.icon className="h-3.5 w-3.5" /> {meta.label}
            </div>
            <div className="text-sm text-neutral-600">{meta.sub}</div>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
            <Input placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 w-52" data-testid={`taxonomy-search-${kind}`} />
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary-800 hover:bg-primary-900 text-white" data-testid={`taxonomy-add-btn-${kind}`}>
                <Plus className="h-4 w-4 mr-1.5" /> Add {meta.label.toLowerCase().slice(0, -1)}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add {meta.label.toLowerCase().slice(0, -1)}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Label</Label>
                  <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="e.g. Photography" data-testid={`taxonomy-new-label-${kind}`} />
                </div>
                <div>
                  <Label className="text-xs">Icon (lucide name or emoji, optional)</Label>
                  <Input value={newIcon} onChange={(e) => setNewIcon(e.target.value)} placeholder="camera" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button onClick={create} disabled={saving || !newLabel.trim()} className="bg-primary-800 hover:bg-primary-900 text-white" data-testid={`taxonomy-create-submit-${kind}`}>
                  {saving ? 'Saving…' : 'Create'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="p-10 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-700" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-neutral-500 text-sm">No items{q ? ' match your search' : ' yet'}.</div>
        ) : (
          <div className="divide-y divide-neutral-100 border border-neutral-200 rounded-xl overflow-hidden">
            {filtered.map((it, i) => (
              <TaxonomyRow
                key={it.id}
                item={it}
                index={i}
                canMoveUp={i > 0}
                canMoveDown={i < filtered.length - 1}
                onMove={move}
                onSave={(body) => patch(it.id, body)}
                onDelete={() => softDelete(it.id)}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const TaxonomyRow = ({ item, index, canMoveUp, canMoveDown, onMove, onSave, onDelete }) => {
  const [label, setLabel] = useState(item.label);
  const [icon, setIcon] = useState(item.icon || '');
  const [dirty, setDirty] = useState(false);

  useEffect(() => { setLabel(item.label); setIcon(item.icon || ''); setDirty(false); }, [item.id, item.label, item.icon]);

  const save = () => {
    if (!dirty) return;
    onSave({ label, icon: icon || null });
    setDirty(false);
  };

  return (
    <div className={`flex items-center gap-3 p-3 ${item.is_active ? 'bg-white' : 'bg-neutral-50/60'}`} data-testid={`taxonomy-row-${item.id}`}>
      <div className="flex flex-col">
        <button onClick={() => onMove(index, -1)} disabled={!canMoveUp} className="disabled:opacity-30 hover:text-primary-900 text-neutral-500"><ChevronUp className="h-4 w-4" /></button>
        <button onClick={() => onMove(index, +1)} disabled={!canMoveDown} className="disabled:opacity-30 hover:text-primary-900 text-neutral-500"><ChevronDown className="h-4 w-4" /></button>
      </div>
      <div className="w-24 shrink-0">
        <Input
          value={icon}
          onChange={(e) => { setIcon(e.target.value); setDirty(true); }}
          onBlur={save}
          placeholder="icon"
          className="text-xs font-mono"
          data-testid={`taxonomy-row-icon-${item.id}`}
        />
      </div>
      <div className="flex-1 min-w-0">
        <Input
          value={label}
          onChange={(e) => { setLabel(e.target.value); setDirty(true); }}
          onBlur={save}
          className={item.is_active ? '' : 'line-through text-neutral-500'}
          data-testid={`taxonomy-row-label-${item.id}`}
        />
        <div className="text-[10px] font-mono text-neutral-400 mt-1">/{item.slug}</div>
      </div>
      <div className="flex items-center gap-2">
        <Switch
          checked={item.is_active}
          onCheckedChange={(v) => onSave({ is_active: !!v })}
          data-testid={`taxonomy-row-active-${item.id}`}
        />
        <span className="text-[10px] uppercase tracking-widest text-neutral-500 w-14">
          {item.is_active ? 'Active' : 'Inactive'}
        </span>
      </div>
      <Button variant="ghost" size="sm" onClick={onDelete} className="text-accent-700 hover:text-accent-900" data-testid={`taxonomy-row-delete-${item.id}`}>
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
};

export default AdminTaxonomiesPage;
